import type { RelinkFailure, RelinkStatus } from './useAggregator'
import { relinkMatchPath } from './relinkRoutes'

/**
 * What a reconnect attempt came to — the two entrances' common input. `started`
 * is a completed `/complete` (the flow hook's `started` state, or the Plaid OAuth
 * return's `relinked`); `relinkStatus: null` is a reconnect that ALREADY existed
 * (a 409 `already_relinked` on the return), whose status is unknown.
 * `failed` is the flow hook's `failed` state, carrying its own fixed copy.
 */
export type RelinkOutcomeInput =
  | { kind: 'started'; newItemId: string; relinkStatus: RelinkStatus | null; institutionName: string | null }
  | { kind: 'failed'; error: RelinkFailure; message: string }

/** What the screen does about it. Data, so the whole mapping is unit-tested
 *  without rendering anything. */
export type RelinkOutcome =
  | { kind: 'toast'; tone: 'success' | 'warning' | 'error'; message: string; refreshAccounts?: boolean }
  | { kind: 'navigate'; to: string }
  /** The user signed in to a DIFFERENT bank; an `AlertDialog` says so. */
  | { kind: 'different_bank'; title: string; body: string }
  /** The bank is connected again — offer the repair flow for `itemId`. */
  | { kind: 'use_reauth'; itemId: string; title: string; body: string }
  /** The user closed the bank window: nothing to say. */
  | { kind: 'silent' }

/** What a reconnect calls a bank whose name is not known. Lowercase: it sits mid-sentence;
 *  `capitalised` lifts it where it opens one. */
export const UNKNOWN_BANK = 'your bank'

/** "{Bank} reconnected." — the toast every surface shows when a reconnect completes,
 *  including the match step's last save. `bank` null reads "Your bank reconnected.". */
export function reconnectedMessage(bank: string | null): string {
  return `${capitalised(bank ?? UNKNOWN_BANK)} reconnected.`
}

/** The match step's standing line once there is nothing left to match. */
export function fullyReconnectedLine(bank: string | null): string {
  return `${capitalised(bank ?? UNKNOWN_BANK)} is fully reconnected.`
}

/** The wording for a reconnect whose session cannot be used. An expired or invalid session
 *  reads the same everywhere: the `/plaid` page (a full page, the OAuth return's context)
 *  and the in-app `nonce_invalid` toast both build from these constants. `session_mismatch`
 *  and `provider_mismatch` are not expiry and keep their own toast. */
export const RECONNECT_EXPIRED_TITLE = 'This reconnect window has expired'
export const RECONNECT_NOT_RESUMED_TITLE = 'This reconnect could not be resumed'
export const RECONNECT_UNUSABLE_BODY =
  'Your bank is still disconnected and nothing was changed. Start the reconnect again from Connections.'

/**
 * THE one status-to-copy mapping for a reconnect. `Connections`, the Link prompt
 * and `PlaidOAuthReturn` all call it; none keeps a table of its own.
 *
 * `bank` is the DISCONNECTED bank the user set out to reconnect, or `null` where
 * that is unknown (the OAuth return has no list to look it up in).
 *
 * **Both switches are exhaustive** (`never`), so a new status or failure kind
 * fails typecheck here instead of falling into a silent default. Only `abandoned`
 * is silent — it is the user's own exit; `link_failed` and `unexpected` are
 * failures the user must see, and retry.
 */
export function relinkOutcomeOf(input: RelinkOutcomeInput, bank: string | null): RelinkOutcome {
  const name = bank ?? UNKNOWN_BANK
  if (input.kind === 'started') {
    switch (input.relinkStatus) {
      case 'pending':
        return {
          kind: 'toast',
          tone: 'success',
          message: `Reconnecting ${name} — your accounts will update shortly.`,
        }
      case 'completed':
        return { kind: 'toast', tone: 'success', message: reconnectedMessage(bank) }
      case 'needs_review':
      case null:
        return { kind: 'navigate', to: relinkMatchPath(input.newItemId) }
      case 'institution_mismatch':
        return {
          kind: 'different_bank',
          title: 'That was a different bank',
          body:
            `${input.institutionName ?? 'The bank you picked'} was added as a new connection. ` +
            `${capitalised(name)} is still disconnected — you can reconnect it later.`,
        }
      default:
        return input.relinkStatus satisfies never
    }
  }

  const { error } = input
  switch (error.kind) {
    case 'abandoned':
      return { kind: 'silent' }
    case 'already_relinked':
      return { kind: 'navigate', to: relinkMatchPath(error.itemId) }
    case 'use_reauth':
      return {
        kind: 'use_reauth',
        itemId: error.itemId,
        title: `${capitalised(name)} is connected again`,
        body: 'Sign in to repair it instead.',
      }
    case 'not_found':
      return { kind: 'toast', tone: 'error', message: 'This connection no longer exists.', refreshAccounts: true }
    case 'nonce_unavailable':
      return {
        kind: 'toast',
        tone: 'error',
        message: "Reconnect isn't available right now. Try again in a few minutes.",
      }
    case 'nonce_invalid':
      return { kind: 'toast', tone: 'error', message: `${RECONNECT_EXPIRED_TITLE}. Start it again.` }
    case 'session_mismatch':
    case 'provider_mismatch':
      return { kind: 'toast', tone: 'error', message: "The reconnect couldn't be finished. Start it again." }
    case 'already_linked':
      return { kind: 'toast', tone: 'error', message: 'That login is already connected in TrackE5.' }
    case 'mfa_required':
      return { kind: 'toast', tone: 'error', message: "This bank can't be connected right now." }
    case 'no_adapter':
      return {
        kind: 'toast',
        tone: 'error',
        message: "This connection method isn't available yet in this app — try again after updating.",
      }
    case 'other':
      return { kind: 'toast', tone: 'error', message: "The reconnect couldn't be started." }
    // The hook's own fixed copy: a failure at the bank window, or a dropped
    // connection. Retryable — the caller resets, so the button is live again.
    case 'link_failed':
    case 'unexpected':
    // Match-step errors; the reconnect start cannot raise them. Generic copy
    // until the match step gives them their own.
    case 'account_changed':
    case 'already_paired':
    case 'relink_completed':
    case 'currency_mismatch':
    case 'too_many':
      return { kind: 'toast', tone: 'error', message: input.message }
    default:
      return error satisfies never
  }
}

function capitalised(name: string): string {
  const first = [...name][0]
  return first === undefined ? name : first.toUpperCase() + name.slice(first.length)
}

/** "{old} ← {new}". `attachedInPlace` (no new account to name) reads the old name alone; a new
 *  account whose name is not known reads "a new account". */
export function alreadyMatchedLine(
  oldName: string | undefined,
  newName: string | undefined,
  attachedInPlace = false,
): string {
  const old = oldName ?? 'An account'
  return attachedInPlace ? old : `${old} ← ${newName ?? 'a new account'}`
}
